// Model from asiga-web-portal

import { ErrorSvg } from "@/components/Icon/Icon";
import { DataObject } from "@/typed/CaseStateData";
import { parseDataToGeometry } from "@/utils/generateModelThumbnail";
import { Box, Typography, useTheme } from "@mui/material";
import { Billboard, Html, useBounds } from "@react-three/drei";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BufferGeometry, DoubleSide, Euler, Vector3 } from "three";

interface Props {
  fileData: DataObject[];
  outsideBounds?: boolean;
  rotation?: Euler;
  position?: Vector3;
  visible?: boolean;
  color?: string;
  showErrorOnFailure?: boolean;
}

export const Model: FC<Props> = ({
  fileData,
  outsideBounds = false,
  position = new Vector3(0, 0, 0),
  rotation = new Euler(0, 0, 0),
  color,
  showErrorOnFailure = true
}) => {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const [geometry, setGeometry] = useState<BufferGeometry | undefined>();
  const [error, setError] = useState<boolean>(false);
  const bounds = useBounds();

  useEffect(() => {
    if (!outsideBounds) {
      bounds.refresh().clip().fit();
    }
  }, [geometry, bounds, outsideBounds]);

  useEffect(() => {
    setError(false);

    const loadFileGeometry = async () => {
      // If no files, clear geometry.
      if (!fileData || fileData.length === 0) {
        setGeometry(undefined);
        setError(true);
        return;
      }

      const geometry = await parseDataToGeometry(fileData);
      setGeometry(geometry);
      setError(geometry === undefined);
    };

    loadFileGeometry();
  }, [fileData]);

  return (
    <>
      {geometry && (
        /* eslint-disable react/no-unknown-property */
        <mesh position={position} rotation={rotation}>
          <primitive
            object={geometry}
            attach="geometry"
            onUpdate={(self: any) => {
              self.computeVertexNormals();
            }}
          />
          <meshStandardMaterial
            side={DoubleSide}
            roughness={0.5}
            color={geometry?.hasAttribute("color") ? "#ffffff" : (color ?? theme.model.model)} // Ambient colour (i.e., tint)
            vertexColors={geometry?.hasAttribute("color") ? true : false}
            attach="material"
          />
        </mesh>
        /* eslint-enable react/no-unknown-property */
      )}
      {showErrorOnFailure && error && !geometry && (
        <Billboard follow={true}>
          <Html as="div" fullscreen pointerEvents="none">
            <Box
              component="div"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                height: "100%",
                padding: "20%",
                boxSizing: "border-box",
                gap: "1em",
                color: theme.palette.warning.main,
                pointerEvents: "none"
              }}
            >
              <ErrorSvg width={64} height={64} />
              <Typography textAlign={"center"} fontSize={"1.25em"} sx={{ color: theme.palette.primary.light }}>
                {t("error.failedToLoadScan")}
              </Typography>
            </Box>
          </Html>
        </Billboard>
      )}
    </>
  );
};
